import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

interface SegmentedProps {
  options: SegmentedOption[];
  isActive: (value: string) => boolean;
  onToggle: (value: string) => void;
  /**
   * `subtle` (default) — quiet track, active segment lifts to a white card.
   * Used for the results filter bar.
   * `solid` — active segment is filled with `primary`. Used where the control
   * is the page's main mode switch and needs to out-weigh the filter bar.
   */
  variant?: "subtle" | "solid";
}

/**
 * Segmented control. Multi-select is allowed on purpose — `onToggle` receives
 * one value and the caller decides whether that replaces the selection or
 * adds to it, because the Horizon filter has to light several segments at
 * once (the spec requires horizons side by side). Don't convert this to a
 * radio group.
 */
export function Segmented({ options, isActive, onToggle, variant = "subtle" }: SegmentedProps) {
  return (
    <div
      className={cn(
        "flex rounded-lg p-[3px]",
        variant === "subtle" ? "border border-border bg-muted" : "border border-border bg-card shadow-sm"
      )}
    >
      {options.map((opt) => {
        const active = isActive(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors",
              active
                ? variant === "subtle"
                  ? "bg-card font-bold text-primary shadow-sm"
                  : "bg-primary font-semibold text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {opt.icon && <opt.icon className="h-3.5 w-3.5 shrink-0" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
